import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { 
  FaTimes, 
  FaQuestionCircle, 
  FaChevronRight,
  FaHome,
  FaCreditCard,
  FaPiggyBank,
  FaHandHoldingUsd,
  FaMobileAlt,
  FaLock,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt
} from 'react-icons/fa';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85) !important;
  background-color: gold !important;
  backdrop-filter: blur(8px);
  display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  z-index: 9999;
  animation: ${fadeIn} 0.2s ease-out;
  padding: 20px;
`;

const ChatContainer = styled.div`
  background: #ffffff;
  width: 90%;
  max-width: 600px;
  height: 80vh;
  max-height: 700px;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  animation: ${slideUp} 0.3s ease-out;
  overflow: hidden;
`;

const ChatHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, var(--gn-color-primary, #821111) 0%, rgba(130, 17, 17, 0.9) 100%);
  color: #ffffff;
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
`;

const HeaderText = styled.div`
  h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    font-family: var(--gn-font-heading);
  }
  p {
    margin: 0;
    font-size: 0.85rem;
    opacity: 0.9;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #ffffff;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: rotate(90deg);
  }
`;

const ChatBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #f8f9fa;
`;

const WelcomeMessage = styled.div`
  background: linear-gradient(135deg, rgba(130, 17, 17, 0.05) 0%, rgba(130, 17, 17, 0.02) 100%);
  border-left: 3px solid var(--gn-color-primary, #821111);
  padding: 16px;
  border-radius: 12px;
  animation: ${slideIn} 0.3s ease-out;
`;

const WelcomeText = styled.p`
  margin: 0;
  color: var(--gn-color-text, #1B1833);
  font-size: 0.95rem;
  line-height: 1.6;
  font-family: var(--gn-font-body);
`;

const QuestionCard = styled.div`
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  animation: ${slideIn} 0.3s ease-out;
  animation-delay: ${props => props.delay || 0}s;
  animation-fill-mode: both;

  &:hover {
    border-color: var(--gn-color-primary, #821111);
    box-shadow: 0 4px 12px rgba(130, 17, 17, 0.1);
    transform: translateX(4px);
  }

  ${props => props.isOpen && `
    border-color: var(--gn-color-primary, #821111);
    box-shadow: 0 4px 12px rgba(130, 17, 17, 0.15);
  `}
`;

const QuestionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const QuestionContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const QuestionIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, var(--gn-color-primary, #821111) 0%, rgba(130, 17, 17, 0.8) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 0.9rem;
  flex-shrink: 0;
`;

const QuestionText = styled.div`
  flex: 1;
  font-weight: 600;
  color: var(--gn-color-text, #1B1833);
  font-size: 0.95rem;
  font-family: var(--gn-font-body);
`;

const ChevronIcon = styled.div`
  color: #999;
  font-size: 0.8rem;
  transition: transform 0.3s ease;
  ${props => props.isOpen && 'transform: rotate(90deg);'}
`;

const AnswerContent = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  color: #666;
  font-size: 0.9rem;
  line-height: 1.6;
  font-family: var(--gn-font-body);
  animation: ${slideIn} 0.2s ease-out;
`;

const QuickActions = styled.div`
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const QuickActionButton = styled.button`
  background: rgba(130, 17, 17, 0.05);
  border: 1px solid rgba(130, 17, 17, 0.2);
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 0.8rem;
  color: var(--gn-color-primary, #821111);
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 600;
  font-family: var(--gn-font-body);

  &:hover {
    background: var(--gn-color-primary, #821111);
    color: #ffffff;
    border-color: var(--gn-color-primary, #821111);
  }
`;

const HelpIcon = styled(FaQuestionCircle)`
  cursor: pointer;
  color: var(--gn-color-primary, #821111) !important;
  font-size: 1.0rem;
  transition: color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--gn-color-accent, #D39D55) !important;
  }

  svg {
    display: block;
  }
`;

const HelpChat = ({ label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openQuestion, setOpenQuestion] = useState(null);
  const navigate = useNavigate();
  const chatBodyRef = useRef(null);

  const faqData = [
    {
      id: 1,
      question: "How do I open a checking account?",
      answer: "You can open a checking account online, by visiting one of our branches, or by calling our customer service. We offer various checking account options to suit your needs.",
      icon: FaCreditCard,
      quickActions: [
        { label: "View Checking Accounts", path: "/checking" },
        { label: "Find a Branch", path: "/location" }
      ]
    },
    {
      id: 2,
      question: "What are the interest rates for savings accounts?",
      answer: "Our savings accounts offer competitive interest rates. Rates vary by account type and balance. Please visit our savings page or contact us for current rates.",
      icon: FaPiggyBank,
      quickActions: [
        { label: "View Savings Accounts", path: "/savings" },
        { label: "Contact Us", path: "/contact" }
      ]
    },
    {
      id: 3,
      question: "How do I apply for a loan?",
      answer: "You can apply for a loan through our online portal, visit a branch, or call our loan specialists. We offer personal loans, business loans, auto loans, and home loans.",
      icon: FaHandHoldingUsd,
      quickActions: [
        { label: "View Loan Options", path: "/loans" },
        { label: "Apply Now", path: "https://portal.gnbank.net" }
      ]
    },
    {
      id: 4,
      question: "How do I access mobile banking?",
      answer: "Download our mobile banking app from the App Store or Google Play. You can also access online banking through our website. Both platforms offer secure access to your accounts.",
      icon: FaMobileAlt,
      quickActions: [
        { label: "Mobile Banking", path: "/MobileBanking" },
        { label: "Online Banking", path: "/OnlineBanking" }
      ]
    },
    {
      id: 5,
      question: "What should I do if I lose my card?",
      answer: "If you lose your card, contact us immediately at our 24/7 hotline or through our customer support portal to report it. We'll cancel the card and issue a replacement.",
      icon: FaLock,
      quickActions: [
        { label: "Contact Support", path: "/contact" },
        { label: "Report Lost Card", path: "/contact" },
        { label: "Customer Support Portal", path: "https://portal.gnbank.net/login?step=signIn" }
      ]
    },
    {
      id: 6,
      question: "Where can I find a branch or ATM?",
      answer: " You can use any Citibank ATM or branch to access your GN Bank accounts. Use our branch locator to find the nearest GN Bank branch or ATM. You can search by address, zip code, or use your current location.",
      icon: FaMapMarkerAlt,
      quickActions: [
        { label: "Find Location", path: "/location" }
      ]
    },
    {
      id: 7,
      question: "How do I contact customer service?",
      answer: "You can reach us by phone, email, or by visiting a branch or using our customer support portal. Our customer service team is available to help with all your banking needs.",
      icon: FaEnvelope,
      quickActions: [
        { label: "Contact Us", path: "/contact" },
        { label: "Find Branch", path: "/location" },
        { label: "Customer Support Portal", path: "https://portal.gnbank.net/login?step=signIn" }
      ]
    },
    {
      id: 8,
      question: "What are your business banking options?",
      answer: "We offer comprehensive business banking solutions including business checking, savings, loans, and credit cards. Visit our business banking section to learn more.",
      icon: FaCreditCard,
      quickActions: [
        { label: "Business Banking", path: "/BusinessChecking" }
      ]
    }
  ];

  const handleQuestionClick = (questionId) => {
    setOpenQuestion(openQuestion === questionId ? null : questionId);
  };

  const handleQuickAction = (path) => {
    if (/^https?:\/\//i.test(path)) {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(path);
    }
    setIsOpen(false);
  };

  const handleClose = () => {
    setIsOpen(false);
    setOpenQuestion(null);
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <>
      {label ? (
        <button
          type="button"
          className="gn-utility-trigger"
          onClick={() => setIsOpen(true)}
          aria-label="Help"
        >
          {label}
        </button>
      ) : (
        <HelpIcon onClick={() => setIsOpen(true)} />
      )}

      <Overlay isOpen={isOpen} onClick={handleClose}>
        <ChatContainer onClick={(e) => e.stopPropagation()}>
          <ChatHeader>
            <HeaderContent>
              <HeaderIcon>
                <FaQuestionCircle />
              </HeaderIcon>
              <HeaderText>
                <h3>How can we help?</h3>
                <p color="#821111" style={{ fontWeight: 'bold', color: 'gold' }}>Frequently asked questions</p>
              </HeaderText>
            </HeaderContent>
            <CloseButton onClick={handleClose} aria-label="Close help">
              <FaTimes />
            </CloseButton>
          </ChatHeader>

          <ChatBody ref={chatBodyRef}>
            <WelcomeMessage>
              <WelcomeText>
                👋 Welcome to GN Bank! Browse our frequently asked questions below or use the quick actions to navigate to specific pages.
              </WelcomeText>
            </WelcomeMessage>

            {faqData.map((faq, index) => {
              const IconComponent = faq.icon;
              const isOpen = openQuestion === faq.id;
              
              return (
                <QuestionCard
                  key={faq.id}
                  isOpen={isOpen}
                  delay={index * 0.05}
                  onClick={() => handleQuestionClick(faq.id)}
                >
                  <QuestionHeader>
                    <QuestionContent>
                      <QuestionIcon>
                        <IconComponent />
                      </QuestionIcon>
                      <QuestionText>{faq.question}</QuestionText>
                    </QuestionContent>
                    <ChevronIcon isOpen={isOpen}>
                      <FaChevronRight />
                    </ChevronIcon>
                  </QuestionHeader>
                  
                  {isOpen && (
                    <AnswerContent>
                      {faq.answer}
                      {faq.quickActions && faq.quickActions.length > 0 && (
                        <QuickActions>
                          {faq.quickActions.map((action, idx) => (
                            <QuickActionButton
                              key={idx}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleQuickAction(action.path);
                              }}
                            >
                              {action.label}
                            </QuickActionButton>
                          ))}
                        </QuickActions>
                      )}
                    </AnswerContent>
                  )}
                </QuestionCard>
              );
            })}
          </ChatBody>
        </ChatContainer>
      </Overlay>
    </>
  );
};

export default HelpChat;

